import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { AiOutlineArrowDown, AiOutlineArrowUp } from "react-icons/ai";
import { Avatar, Badge, Group, Paper, Stack, Text } from "@mantine/core";
import styles from "./styles/PodcastListItem.module.css";
import { getPodcastImage, ImageSizes } from "@/core/helpers";

type LoadingType = "eager" | "lazy";

interface PodcastListItemProps {
    podcast: Podcast;
    addon?: ReactNode | null;
    movement?: ReactNode | null;
    showReactions?: boolean;
    suffix?: ReactNode | null;
    imageLoading?: LoadingType;
    anchor?: string;
}

export const PodcastListItem = ({
    podcast,
    addon = null,
    movement = null,
    showReactions = false,
    suffix = null,
    imageLoading = "lazy",
    anchor = "",
}: PodcastListItemProps) => {
    const extras: Record<string, string> = {};
    if (anchor) {
        extras.id = anchor;
    }

    return (
        <Paper
            className={addon ? `${styles.item} ${styles.rankedItem}` : styles.item}
            component={Link}
            p={addon ? "sm" : "md"}
            radius={addon ? 0 : "sm"}
            shadow={addon ? undefined : "xs"}
            to={`/podd/${podcast.slug}`}
            withBorder={!addon}
            {...extras}>
            <Group align="center" className={addon ? styles.rankedGroup : undefined} gap="sm" wrap="nowrap">
                {addon && <div className={styles.addon}>{addon}</div>}
                <Avatar
                    className={styles.catimg}
                    imageProps={{ loading: imageLoading }}
                    radius="sm"
                    size={addon ? 56 : 72}
                    src={getPodcastImage(podcast, ImageSizes.Thumb)}>
                    {podcast.name.slice(0, 1)}
                </Avatar>

                <Stack className={styles.label} gap={6}>
                    <Text className={styles.name} fw={600}>
                        {podcast.name}
                    </Text>

                    {podcast.categories && (
                        <Group gap={6}>
                            {podcast.categories
                                .filter((c: Category) => c.slug !== "podcaster")
                                .slice(0, addon ? 2 : undefined)
                                .map((c: Category, i: number) => (
                                    <Badge key={`subcat-${c.id}-${i}`} radius="sm" size="sm" variant="light">
                                        {c.name}
                                    </Badge>
                                ))}
                        </Group>
                    )}

                    {podcast.author && (
                        <Text c="dimmed" size="sm">
                            {podcast.author.name}
                        </Text>
                    )}

                    {!!podcast.episodes_count && (
                        <Text c="dimmed" size="xs">
                            {podcast.episodes_count} episoder
                        </Text>
                    )}

                    {showReactions && (
                        <Group c="dimmed" gap={2}>
                            <AiOutlineArrowUp size={18} />
                            <AiOutlineArrowDown size={18} />
                        </Group>
                    )}
                </Stack>

                {(movement || suffix) && (
                    <div className={styles.trailing}>
                        {movement && <div className={styles.movement}>{movement}</div>}
                        {suffix && <div className={styles.suffix}>{suffix}</div>}
                    </div>
                )}
            </Group>
        </Paper>
    );
};
